import { Typography, Card, Badge, CardContent } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { FilterType, TableHeaderType } from "@/components/tables/types";
import { useTags } from "./useTags";
import { Tag } from "./types";
import { Paginate } from "@/components/paginate";
import { RelativeComponentLoader } from "@/components/loader";
import { Stack } from "@altimateai/lego";
import DeleteTag from "./components/DeleteTag";
import EditTagButton from "./components/EditTagButton";
import CreateTag from "./components/CreateTag";
import SearchTags from "./components/SearchTags";
import { useMemo } from "react";
import { useUsers } from "@/helpers/hooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { FilterTagWrapper } from "@/components/tags";
import { FilterApplicableFor } from "@/components/tags/types";
import { NoTagsIcon } from "@/assets/images";
import { isOwner } from "@/services/auth";
import { formatUserName } from "@/helpers/utils";
import { useBreadcrumb } from "@/components/breadcrumbs";

const Tags = () => {
  useBreadcrumb("Tags", {
    defaultParents: [{ label: "Settings", pathname: "/settings", noLink: true }],
  });
  const { searchParams, updateSearchParamsByKey, deleteSearchParamsByKeyValue } =
    useFiltersBySearchParams();
  const { page, isLoading, tags, pageSize, total, totalPages } = useTags({ mode: "paginated" });
  const { getById, getFormattedUserNameById, users } = useUsers();
  const filterOwners = searchParams.getAll("owners");

  const setPage = (newPage: number) => {
    updateSearchParamsByKey({ page: (newPage + 1).toString() });
  };

  const setPageSize = (newPageSize: number) => {
    updateSearchParamsByKey({ pageSize: newPageSize.toString(), page: "1" });
  };

  const onOwnerChange = (value: unknown) => {
    updateSearchParamsByKey({ owners: value as string[], page: "1" });
  };

  const owners = useMemo(() => {
    const uniqueOwnerIds = Array.from(new Set(tags.map(tag => tag.created_by)));
    return uniqueOwnerIds
      ?.map(item => getById(item))
      .map(user => ({ label: formatUserName(user), value: user?.id.toString() }))
      .filter((u): u is { label: string; value: string } => u.value !== undefined);
  }, [tags, getById]);

  const header: TableHeaderType<Tag>[] = [
    {
      id: 1,
      uid: "name",
      label: "Tag Name",
      render: item => <div>{(item as Tag).display_name}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
    },
    {
      id: 2,
      uid: "description",
      label: "Description",
      render: item => <div>{(item as Tag).description}</div>,
    },
    {
      id: 3,
      uid: "type",
      label: "Type",
      render: item => (
        <div className="flex gap-2">
          <Badge variant="outline">{(item as Tag).tag_type}</Badge>
        </div>
      ),
    },
    {
      id: 4,
      uid: "usage",
      label: "Usage",
      render: item => <div>{(item as Tag).usage}</div>,
    },
    {
      id: 5,
      uid: "owner",
      label: "Owner",
      render: item => {
        return <Badge variant="outline">{getFormattedUserNameById(item.created_by)}</Badge>;
      },
      filter: {
        filterType: FilterType.DROPDOWN,
        onChange: onOwnerChange,
        options: owners || [],
        value: filterOwners,
        id: "owner",
      },
    },
    {
      id: 6,
      uid: "actions",
      label: "Actions",
      render: item =>
        isOwner() ? (
          <div className="flex gap-2">
            {item?.rk ? (
              <>
                <EditTagButton tag={item as Tag} />
                <DeleteTag id={item.rk} name={item.display_name} />
              </>
            ) : null}
          </div>
        ) : null,
    },
  ];

  return (
    <div className="al-p-6">
      <Stack justify="between" align="center" className="al-mb-6">
        <Typography variant="page-heading">Tags</Typography>
        <Stack>
          <SearchTags />
          <CreateTag />
        </Stack>
      </Stack>

      {isLoading ? (
        <RelativeComponentLoader label="Loading tags..." />
      ) : total ? (
        <>
          <div className="al-mb-4">
            <FilterTagWrapper
              applicableFor={FilterApplicableFor.TAGS}
              filters={{
                ownerFilter: {
                  filterStr: filterOwners
                    .map(owner => getFormattedUserNameById(Number(owner)))
                    .filter((name): name is string => name !== null),
                  label: "Owners",
                  onclose: (filterStr?: string) => {
                    deleteSearchParamsByKeyValue({
                      owners: users?.find(u => formatUserName(u) === filterStr)?.id.toString(),
                    });
                  },
                },
              }}
            />
          </div>
          <DataTable<Tag>
            header={header}
            data={{ items: tags }}
            onRowClick={item => console.log("clicked", item as Tag)}
          />
          <Paginate
            itemCount={total}
            page={page ? page - 1 : 0}
            pageSize={pageSize}
            numPages={totalPages}
            onPageClick={setPage}
            onPageSizeChange={setPageSize}
            showPageSize
          />
        </>
      ) : (
        <Card className="al-p-4">
          <CardContent className="al-h-full al-pt-8 al-bg-gray-10">
            <Stack
              direction="column"
              gap="xl"
              justify="center"
              align="center"
              className="al-h-full"
            >
              <NoTagsIcon />
              <Typography variant="h4">No tags found</Typography>
              <CreateTag />
            </Stack>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Tags;
